import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BlogLayout } from '../../components/BlogLayout';
import { PageHeader } from '../../components/PageHeader';
import { ArchiveTable } from '../../components/ArchiveTable';
import { api } from '../../lib/api';
import type { Post } from '../../lib/types';
import { slugifyTag, matchesTagSlug } from '../../lib/tagSlug';

function formatTagLabel(slug: string, matches: string[]) {
  const matchedLabel = matches.find((tag) => matchesTagSlug(tag, slug));
  if (matchedLabel) return matchedLabel;
  const readable = slug.replace(/-/g, ' ').trim();
  return readable || slug;
}

export default function TagPage() {
  const { slug } = useParams<{ slug: string }>();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const tagSlug = slugifyTag(slug || '') || 'tag';

  useEffect(() => {
    async function fetchPosts() {
      try {
        const response = await api.get<Post[]>('/api/v1/posts');
        if (response.success && response.data) {
          setPosts(response.data);
        } else {
          setError(response.error?.message || 'Failed to load posts');
        }
      } catch (err) {
        setError('Failed to load posts');
      } finally {
        setLoading(false);
      }
    }
    fetchPosts();
  }, []);

  const matchingPosts = posts.filter((post) =>
    (post.tags || []).some((tag) => matchesTagSlug(tag, tagSlug))
  );

  const tagPool = matchingPosts.flatMap((post) => post.tags || []);
  const tagLabel = formatTagLabel(tagSlug, tagPool);
  const pageTitle = `Posts tagged "${tagLabel}"`;

  return (
    <BlogLayout>
      <Helmet>
        <title>Posts tagged "{tagLabel}" - QuestFlow</title>
        <meta name="description" content={`Browse QuestFlow coverage tagged ${tagLabel}`} />
      </Helmet>
      <div className="page-body archive-page">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading posts...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>{error}</p>
          </div>
        ) : (
          <>
            <PageHeader title={pageTitle} eyebrow="Tag" />
            <ArchiveTable posts={matchingPosts} initialQuery={tagLabel} />
          </>
        )}
      </div>
    </BlogLayout>
  );
}
